import { useRef, useState, useEffect, useContext } from "react";
import { LoadingContext, UserContext } from "../../../context/handleContext";
import axios from "axios";
import { api } from "../../../utils/defaults";
import { toast } from "react-toastify";
import { VncScreen } from "react-vnc";
import Chip from "./chip";
import { 
  Box, 
  Button, 
  CircularProgress, 
  Modal, 
  Stack, 
  useMediaQuery, 
  useTheme,
  Container
} from "@mui/material";

import ReplayIcon from '@mui/icons-material/Replay';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import Network from "./network";
import FilterComponent from "../../../utils/filter";

export default function DeviceMonitor(props) {
  const [loading, setLoading] = useContext(LoadingContext);
  const [user] = useContext(UserContext);
  const [list, setList] = useState([]);
  const [device, setDevice] = useState({});
  const [loadingvnc, setLoadingvnc] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [filtered, setFiltered] = useState([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  const update = (idtabelapreco = undefined) => {
    const controller = new AbortController();

    setLoading(true);
    axios
      .get(`${api}/api/manage/smartdisplay/device${idtabelapreco ? `?idtabelapreco=${idtabelapreco}` : ''}`, {
        headers: {
          Authorization: user.token
        },
        signal: controller.signal
      })
      .then(({ data }) => {
        if (idtabelapreco) {
          // Handle single device update if needed
        } else {
          setList(data);
        }
      })
      .catch(({ response }) => toast.error(response?.data))
      .finally(() => setLoading(false));

    return controller;
  };

  useEffect(() => {
    update();
  }, []);

  const vncScreenRef = useRef();
  const networkRef = useRef();

  // Calcula o número ideal de colunas baseado no tamanho da tela
  const getGridColumns = () => {
    if (isMobile) return 1;
    if (isTablet) return expanded ? 2 : 3;
    return expanded ? 3 : 4;
  };

  const gridTemplateColumns = `repeat(auto-fill, minmax(${expanded ? '340px' : '220px'}, 1fr))`;

  return (
    <Box
      maxWidth='xl'
      disableGutters
      sx={{
        height: 'calc(100vh - 64px)',
        overflowY: 'auto',
        p: isMobile ? 0 : 2,
        display: 'flex',
        flexDirection: 'column',
        overflowX:'hidden',
        maxWidth: '100vw'
      }}
    >
      {/* Componente de Rede */}
      <Network ref={networkRef} />

      {/* Modal VNC */}
      <Modal
        open={device.vnc}
        onClose={() => {
          vncScreenRef.current?.disconnect();
          setDevice({});
          setLoadingvnc(false);
        }}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <Box
          sx={{
            width: '95vw',
            height: "95vh",
            bgcolor: 'background.paper',
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              p: 1,
              bgcolor: 'background.default'
            }}
          >
            <Button
              color='error'
              onClick={() => {
                vncScreenRef.current?.disconnect();
                setDevice({});
                setLoadingvnc(false);
              }}
            >
              Fechar
            </Button>
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              bgcolor: 'black',
              position: 'relative'
            }}
          >
            {loadingvnc && (
              <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%' }} />
            )}
            <VncScreen
              url={`wss://${'wsvnc.allon.cloud'}?idtabelapreco=${device.idtabelapreco}&auth=${user.token}`}
              scaleViewport
              resizeSession
              autoConnect
              ref={vncScreenRef}
              style={{
                width: '100%',
                height: '100%'
              }}
              onCredentialsRequired={e => {
                vncScreenRef.current?.sendCredentials({
                  username: '',
                  password: '102030',
                  target: `wss://${'wsvnc.allon.cloud'}?idtabelapreco=${device.idtabelapreco}&auth=${user.token}`
                });
              }}
              onConnect={() => {
                toast.success('Conectado');
                setLoadingvnc(false);
              }}
              onSecurityFailure={() => {
                toast.error('Autenticação negada');
                setLoadingvnc(false);
              }}
            />
          </Box>
        </Box>
      </Modal>

      {/* Filtros e Controles */}
      <Box sx={{ mb: 2 }}>
        <FilterComponent
          onFilter={setFiltered}
          attributes={[
            'area_venda',
            'idtabelapreco',
            'ip',
            'mac',
            'loja',
            'is_used',
            'device',
            'application',
            'tipo_display'
          ]}
          attributeName={{
            'area_venda': 'Área de venda',
            'idtabelapreco': 'ID',
            'ip': 'IP',
            'mac': 'MAC',
            'loja': 'Loja',
            'is_used': 'Em uso',
            'device': 'Disp. Online',
            'application': 'App. Online',
            'tipo_display': 'Tipo Display'
          }}
          items={list}
          sx={{ p: isMobile ? 0 : 1 }}
          startButton={
            <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
              <Button
                startIcon={<ReplayIcon />}
                variant='outlined'
                fullWidth
                onClick={() => update()}
                disabled={loading}
              >
                Atualizar
              </Button>

              <Button
                startIcon={expanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                variant='outlined'
                fullWidth
                color='primary'
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Retrair' : 'Expandir'}
              </Button>
            </Stack>
          }
        />
      </Box>

      {/* Grid de Dispositivos */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: gridTemplateColumns,
          gap: 2,
          alignContent: 'flex-start',
          pb: isMobile ? 1 : 2
        }}
      >
        {filtered.map((device, index) => (
          <Chip
            key={`${device.idtabelapreco}-${index}`}
            vnc={() => {
              setDevice({ ...device, vnc: true });
              setTimeout(() => setLoadingvnc(true), 500);
            }}
            data={device}
            expanded={expanded}
            network={() => networkRef.current?.open(device)}
          />
        ))}
      </Box>
    </Box>
  );
}